import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ChatIcon from '@/assets/ chat/ChatTeardropDots.svg';
import SendIcon from '@/assets/ chat/PaperPlaneRight.svg';
import { STORAGE_KEYS } from '@/constants';
import { setCredentials } from '@/store/slices/authSlice';
import {
  sendChatbotMessage,
  getChatbotMessages,
  getChatbotConversations,
  archiveChatbotConversation,
} from '@/services/chatbotService';
import './ChatbotWidget.css';

/**
 * Floating "AI Assistant" FAB (bottom-right). Opens a full-screen modal chat
 * panel on DashboardLayout pages. Backed by POST /api/chatbot/message
 * (see biz-backend AICHATBOT.md). companyId always comes from the Tally
 * company switcher (Redux selectedCompanyId / localStorage tally_selected_company_id).
 */
const SELECTED_COMPANY_STORAGE_KEY = 'tally_selected_company_id';
const SUGGESTED_QUESTIONS_FULL = [
  'Last 12 month sales kitna tha?',
  'Last month ka total sales kitna tha?',
  'Kal ka sales kitna hua?',
  'Is week vs last week sales?',
  'Is month vs last month sales?',
  'Sabse zyada bikne wala item kaunsa hai?',
  'Kis party ka paisa pending hai?',
];
const SUGGESTED_QUESTIONS_COMPACT = [
  'Is week vs last week sales?',
  'Is month vs last month sales?',
  'Sabse zyada bikne wala item kaunsa hai?',
];
const DIST_ONLY_QUESTION = 'Kis party ka paisa pending hai?';
const CHANNEL_LABEL_LINES = new Set(['super stockist', 'sub-stockist', 'sub stockist', 'distributor']);

function renderChatMessageContent(content) {
  if (content == null || content === '') return content;
  const text = String(content);
  if (!text.includes('\n')) return text;

  const lines = text.split('\n');
  return lines.map((line, index) => {
    const normalized = line.trim().replace(/:+$/, '').toLowerCase();
    const isChannelLabel = CHANNEL_LABEL_LINES.has(normalized);
    return (
      <span key={index}>
        {isChannelLabel ? <strong className="chatbot-widget__channel-label">{line}</strong> : line}
        {index < lines.length - 1 ? '\n' : null}
      </span>
    );
  });
}
const FOLLOW_UP_SUGGESTIONS = {
  'Last 12 month sales kitna tha?': [
    'Last month ka total sales kitna tha?',
    'Is month vs last month sales?',
    'Is week vs last week sales?',
  ],
  'Last month ka total sales kitna tha?': [
    'Is month vs last month sales?',
    'Is week vs last week sales?',
    'Sabse zyada bikne wala item kaunsa hai?',
  ],
  'Kal ka sales kitna hua?': [
    'Last month ka total sales kitna tha?',
    'Is week vs last week sales?',
    'Is month vs last month sales?',
  ],
  'Is week vs last week sales?': [
    'Is month vs last month sales?',
    'Sabse zyada bikne wala item kaunsa hai?',
    'Kal ka sales kitna hua?',
  ],
  'Is month vs last month sales?': [
    'Is week vs last week sales?',
    'Last month ka total sales kitna tha?',
    'Sabse zyada bikne wala item kaunsa hai?',
  ],
  'Sabse zyada bikne wala item kaunsa hai?': [
    'Last 6 month Sabse zyada bikne wala item kaunsa hai?',
    'Last month sabse zyada bikne wala item kaunsa hai?',
    'Is month vs last month sales?',
  ],
  'Kis party ka paisa pending hai?': [
    'Last month ka total sales kitna tha?',
    'Is month vs last month sales?',
    'Sabse zyada bikne wala item kaunsa hai?',
  ],
};

const getStoredTallyCompanyId = () => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(SELECTED_COMPANY_STORAGE_KEY);
    if (stored == null || stored === '') return null;
    const n = Number(stored);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
};

const normalizeQuestion = question =>
  (question || '')
    .toString()
    .toLowerCase()
    .replace(/[?.,!]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const unwrapConversationList = payload => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const pickLatestConversationForCompany = (threads, companyId) => {
  const id = Number(companyId);
  if (!Number.isFinite(id) || id <= 0) return null;
  const latest = threads.find(
    thread => Number(thread.companyId ?? thread.company_id) === id
  );
  if (!latest) return null;
  const status = String(latest.status || 'active').toLowerCase();
  return status === 'active' ? latest : null;
};

const CHATBOT_BLOCKED_MESSAGE = 'Chatbot access is blocked. Contact admin to unlock.';

const readStoredChatbotBlocked = () => {
  if (typeof window === 'undefined') return false;
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEYS.USER_DATA) || 'null');
    return !!stored?.chatbot_blocked;
  } catch {
    return false;
  }
};

const ChatbotWidget = () => {
  // Same company as Tally Company Switcher — never fall back to dms/myCompany
  // (those can point at a different company and return wrong Tally data).
  const companyId = useSelector(state => {
    const fromRedux = state.tallyReports?.selectedCompanyId;
    if (fromRedux != null && fromRedux !== '') {
      const n = Number(fromRedux);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return getStoredTallyCompanyId();
  });
  const myCompany = useSelector(state => state.companies?.myCompany);
  const dmsCompany = useSelector(state => state.companies?.dmsCompany);
  const user = useSelector(state => state.auth?.user);
  const dispatch = useDispatch();
  const [chatbotBlocked, setChatbotBlocked] = useState(
    () => !!user?.chatbot_blocked || readStoredChatbotBlocked()
  );
  const persistChatbotBlocked = useCallback(() => {
    setChatbotBlocked(true);
    const nextUser = { ...(user || {}), chatbot_blocked: true };
    dispatch(setCredentials({ user: nextUser }));
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEYS.USER_DATA) || '{}');
      localStorage.setItem(
        STORAGE_KEYS.USER_DATA,
        JSON.stringify({ ...stored, chatbot_blocked: true })
      );
    } catch {
      localStorage.setItem(
        STORAGE_KEYS.USER_DATA,
        JSON.stringify({ chatbot_blocked: true })
      );
    }
  }, [dispatch, user]);
  const isDistributor = (
    myCompany?.role_name ||
    myCompany?.role ||
    user?.role ||
    user?.role_name ||
    ''
  )
    .toString()
    .toLowerCase() === 'distributor';

  const selectedCompanyName = (() => {
    if (!companyId) return null;
    const idStr = String(companyId);
    const matchesId = company => company && String(company.id) === idStr;
    const nameOf = company =>
      company?.company_name || company?.name || null;

    if (matchesId(dmsCompany)) return nameOf(dmsCompany);
    const subsidiaries = Array.isArray(dmsCompany?.subsidiaries)
      ? dmsCompany.subsidiaries
      : [];
    const matchedSub = subsidiaries.find(matchesId);
    if (matchedSub) return nameOf(matchedSub);
    if (matchesId(myCompany)) return nameOf(myCompany);
    return null;
  })();

  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [error, setError] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const panelRef = useRef(null);
  const bodyRef = useRef(null);
  const messagesEndRef = useRef(null);
  const hasLoadedHistoryRef = useRef(false);
  const shouldStickToBottomRef = useRef(true);
  const pendingScrollRestoreRef = useRef(null);
  const hasMoreRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const conversationIdRef = useRef(null);
  const messagesRef = useRef([]);

  const storageKey = companyId ? `chatbot_conversation_${companyId}` : null;

  const closeChat = () => {
    setIsOpen(false);
    setIsExpanded(false);
  };

  const openChat = () => {
    shouldStickToBottomRef.current = true;
    setIsExpanded(false);
    setIsOpen(true);
  };

  // Escape: collapse fullscreen first, then close. Lock scroll only when expanded.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = event => {
      if (event.key !== 'Escape') return;
      if (isExpanded) {
        setIsExpanded(false);
      } else {
        closeChat();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, isExpanded]);

  useEffect(() => {
    if (!isOpen || !isExpanded) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, isExpanded]);

  // Compact mode: close when clicking outside the panel
  useEffect(() => {
    if (!isOpen || isExpanded) return undefined;
    const handleClickOutside = event => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        closeChat();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, isExpanded]);

  useEffect(() => {
    if (user?.chatbot_blocked) setChatbotBlocked(true);
  }, [user?.chatbot_blocked]);

  // Resolve this company's latest thread from the server (survives logout/login).
  // Fall back to the localStorage pointer if the list call fails.
  useEffect(() => {
    if (chatbotBlocked) return undefined;

    hasLoadedHistoryRef.current = false;
    shouldStickToBottomRef.current = true;
    setMessages([]);
    setHasMore(false);

    if (!storageKey || !companyId) {
      setConversationId(null);
      return undefined;
    }

    let cancelled = false;
    getChatbotConversations()
      .then(result => {
        if (cancelled) return;
        const threads = unwrapConversationList(result);
        const latest = pickLatestConversationForCompany(threads, companyId);
        if (latest?.id) {
          localStorage.setItem(storageKey, latest.id);
          setConversationId(latest.id);
          return;
        }
        if (storageKey) localStorage.removeItem(storageKey);
        setConversationId(null);
      })
      .catch(() => {
        if (cancelled) return;
        setConversationId(localStorage.getItem(storageKey));
      });

    return () => {
      cancelled = true;
    };
  }, [storageKey, companyId, chatbotBlocked]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Hydrate the most recent page of an existing thread the first time the
  // panel is opened for this company.
  useEffect(() => {
    if (!isOpen || chatbotBlocked || hasLoadedHistoryRef.current || !conversationId) return;
    hasLoadedHistoryRef.current = true;
    shouldStickToBottomRef.current = true;
    setIsLoadingHistory(true);
    getChatbotMessages(conversationId)
      .then(result => {
        const data = result?.data || result;
        setMessages(data?.messages || []);
        setHasMore(!!data?.hasMore);
      })
      .catch(async () => {
        if (storageKey) localStorage.removeItem(storageKey);
        try {
          const result = await getChatbotConversations();
          const threads = unwrapConversationList(result);
          const latest = pickLatestConversationForCompany(threads, companyId);
          hasLoadedHistoryRef.current = false;
          if (latest?.id && latest.id !== conversationId) {
            if (storageKey) localStorage.setItem(storageKey, latest.id);
            setConversationId(latest.id);
            return;
          }
        } catch {
          // Fall through to a fresh thread.
        }
        setConversationId(null);
      })
      .finally(() => setIsLoadingHistory(false));
  }, [isOpen, conversationId, storageKey, companyId, chatbotBlocked]);

  const handleLoadOlder = useCallback(async () => {
    const activeConversationId = conversationIdRef.current;
    const currentMessages = messagesRef.current;
    if (
      !activeConversationId ||
      currentMessages.length === 0 ||
      isLoadingMoreRef.current ||
      !hasMoreRef.current
    ) {
      return;
    }

    const oldestId = currentMessages[0]?.id;
    if (oldestId == null || String(oldestId).startsWith('local-')) return;

    const el = bodyRef.current;
    if (el) {
      pendingScrollRestoreRef.current = {
        prevHeight: el.scrollHeight,
        prevTop: el.scrollTop,
      };
    }
    shouldStickToBottomRef.current = false;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      const result = await getChatbotMessages(activeConversationId, { before: oldestId });
      const data = result?.data || result;
      const older = data?.messages || [];
      setMessages(prev => {
        const existing = new Set(prev.map(m => m.id));
        const unique = older.filter(m => !existing.has(m.id));
        return [...unique, ...prev];
      });
      setHasMore(!!data?.hasMore && older.length > 0);
    } catch {
      pendingScrollRestoreRef.current = null;
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, []);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    const pending = pendingScrollRestoreRef.current;
    if (pending && el) {
      el.scrollTop = pending.prevTop + (el.scrollHeight - pending.prevHeight);
      pendingScrollRestoreRef.current = null;
      return;
    }
    if (isOpen && shouldStickToBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [messages, isOpen, isSending]);

  const handleBodyScroll = () => {
    const el = bodyRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldStickToBottomRef.current = distanceFromBottom < 80;

    if (el.scrollTop < 80 && el.scrollHeight > el.clientHeight + 40) {
      handleLoadOlder();
    }
  };

  const sendQuestion = async rawMessage => {
    const trimmed = rawMessage.trim();
    if (!trimmed || isSending || chatbotBlocked) return;

    if (!companyId) {
      setError('Select a company first to chat with the assistant.');
      return;
    }

    setError(null);
    shouldStickToBottomRef.current = true;
    const optimisticUserMessage = { id: `local-${Date.now()}`, role: 'user', content: trimmed };
    setMessages(prev => [...prev, optimisticUserMessage]);
    setInput('');
    setIsSending(true);

    try {
      const result = await sendChatbotMessage({
        message: trimmed,
        companyId,
        conversationId,
      });
      const data = result?.data || result;

      if (data?.conversationId && data.conversationId !== conversationId) {
        setConversationId(data.conversationId);
        if (storageKey) localStorage.setItem(storageKey, data.conversationId);
      }

      setMessages(prev => [
        ...prev,
        { id: `local-${Date.now()}-reply`, role: 'assistant', content: data?.reply || '...' },
      ]);

      if (data?.chatbotBlocked) {
        persistChatbotBlocked();
        setError(CHATBOT_BLOCKED_MESSAGE);
      }
    } catch (err) {
      const errorCode = err.response?.data?.error?.code;
      if (errorCode === 'CHATBOT_BLOCKED') {
        persistChatbotBlocked();
        setError(CHATBOT_BLOCKED_MESSAGE);
        return;
      }
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleSend = async e => {
    e.preventDefault();
    await sendQuestion(input);
  };

  const handleSuggestionClick = async question => {
    await sendQuestion(question);
  };

  const handleClearChat = async () => {
    const idToArchive = conversationId;
    setMessages([]);
    setHasMore(false);
    setIsLoadingMore(false);
    setError(null);
    setConversationId(null);
    hasLoadedHistoryRef.current = false;
    shouldStickToBottomRef.current = true;
    if (storageKey) localStorage.removeItem(storageKey);

    if (idToArchive && !String(idToArchive).startsWith('local-')) {
      try {
        await archiveChatbotConversation(idToArchive);
      } catch {
        // UI already cleared; next send starts a new active thread.
      }
    }
  };

  const hasMessages = messages.length > 0;
  const latestUserMessage = [...messages].reverse().find(msg => msg.role === 'user')?.content || '';
  const normalizedLatestQuestion = normalizeQuestion(latestUserMessage);
  const followUpSuggestions =
    Object.entries(FOLLOW_UP_SUGGESTIONS).find(
      ([question]) => normalizeQuestion(question) === normalizedLatestQuestion
    )?.[1] || [];
  const baseSuggestions = hasMessages
    ? (followUpSuggestions.length > 0 ? followUpSuggestions : SUGGESTED_QUESTIONS_COMPACT)
    : SUGGESTED_QUESTIONS_FULL;
  const suggestionsToShow = (isDistributor
    ? baseSuggestions
    : baseSuggestions.filter(question => question !== DIST_ONLY_QUESTION)
  ).filter(question => normalizeQuestion(question) !== normalizedLatestQuestion);
  const suggestionLabel = hasMessages
    ? (followUpSuggestions.length > 0 ? 'Try next' : 'Suggestions')
    : 'Ask about';
  const disableClear = isSending || isLoadingHistory || (!conversationId && messages.length === 0);

  return (
    <div className={`chatbot-widget${isExpanded ? ' chatbot-widget--expanded' : ''}`}>
      {isOpen && (
        <>
          {isExpanded && (
            <div
              className="chatbot-widget__overlay"
              onClick={closeChat}
              aria-hidden="true"
            />
          )}
          <div
            className={`chatbot-widget__panel${isExpanded ? ' chatbot-widget__panel--expanded' : ''}`}
            ref={panelRef}
            role="dialog"
            aria-modal={isExpanded ? 'true' : undefined}
            aria-label="Bizz+ AI Assistant"
          >
            <div className="chatbot-widget__header">
              <div className="chatbot-widget__header-title">
                <span>Bizz+ AI Assistant</span>
                {selectedCompanyName && (
                  <span className="chatbot-widget__header-company" title={selectedCompanyName}>
                    {selectedCompanyName}
                  </span>
                )}
              </div>
              <div className="chatbot-widget__header-actions">
                <button
                  type="button"
                  className="chatbot-widget__clear"
                  onClick={handleClearChat}
                  disabled={disableClear}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="chatbot-widget__expand"
                  onClick={() => setIsExpanded(prev => !prev)}
                  aria-label={isExpanded ? 'Exit full screen' : 'Expand to full screen'}
                  title={isExpanded ? 'Exit full screen' : 'Expand'}
                >
                  <i className={`bi ${isExpanded ? 'bi-fullscreen-exit' : 'bi-arrows-fullscreen'}`} />
                </button>
                <button
                  type="button"
                  className="chatbot-widget__close"
                  onClick={closeChat}
                  aria-label="Close chat"
                >
                  <i className="bi bi-x-lg" />
                </button>
              </div>
            </div>

            <div
              className="chatbot-widget__body"
              ref={bodyRef}
              onScroll={handleBodyScroll}
            >
              {isLoadingHistory && (
                <div className="chatbot-widget__hint">Loading conversation...</div>
              )}

              {!isLoadingHistory && isLoadingMore && (
                <div className="chatbot-widget__load-older" aria-live="polite">
                  Loading older messages...
                </div>
              )}

              {!isLoadingHistory && chatbotBlocked && (
                <div className="chatbot-widget__blocked" role="alert">
                  {CHATBOT_BLOCKED_MESSAGE}
                </div>
              )}

              {!isLoadingHistory && !chatbotBlocked && messages.length === 0 && (
                <div className="chatbot-widget__hint">
                  Ask me about your sales, top items, or party balances — in English or Hindi.
                  <br />
                  e.g. &quot;Last month ka top selling item kya tha?&quot;
                </div>
              )}

              {messages.map(msg => (
                <div key={msg.id} className={`chatbot-widget__bubble chatbot-widget__bubble--${msg.role}`}>
                  {msg.role === 'assistant' ? renderChatMessageContent(msg.content) : msg.content}
                </div>
              ))}

              {isSending && (
                <div className="chatbot-widget__bubble chatbot-widget__bubble--assistant chatbot-widget__bubble--typing">
                  <span />
                  <span />
                  <span />
                </div>
              )}

              {error && <div className="chatbot-widget__error">{error}</div>}
              <div ref={messagesEndRef} />
            </div>

            {!chatbotBlocked && !isLoadingHistory && suggestionsToShow.length > 0 && (
              <div className="chatbot-widget__suggestions-block">
                <div className="chatbot-widget__suggestion-label">{suggestionLabel}</div>
                <div
                  className="chatbot-widget__suggestion-row"
                  role="group"
                  aria-label={suggestionLabel}
                >
                  {suggestionsToShow.map(question => (
                    <button
                      key={question}
                      type="button"
                      className="chatbot-widget__suggestion-chip"
                      onClick={() => handleSuggestionClick(question)}
                      disabled={isSending}
                      title={question}
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <form className="chatbot-widget__input-row" onSubmit={handleSend}>
              <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder={chatbotBlocked ? 'Chatbot is blocked' : 'Type your question...'}
                disabled={isSending || chatbotBlocked}
              />
              <button type="submit" disabled={isSending || chatbotBlocked || !input.trim()} aria-label="Send">
                <img src={SendIcon} alt="" />
              </button>
            </form>
          </div>
        </>
      )}

      {!isOpen && (
        <button
          type="button"
          className="chatbot-widget__fab"
          onClick={openChat}
          aria-label="Open AI Assistant"
        >
          <img src={ChatIcon} alt="" />
        </button>
      )}
    </div>
  );
};

export default ChatbotWidget;
